/** Role name used when the bulk-add has to create the demo evaluation role. */
export const PRESENTER_ROLE_NAME = 'Presenter'

/** Role name used for the seating role that sits alongside the presenter. */
export const ATTENDEE_ROLE_NAME = 'Attendee'

type PlannableRole = {
  id: string
  roleName: string
  isPresenter: boolean
}

export type DemoRolePlan = {
  presenter:
    | { action: 'reuse'; roleId: string }
    | { action: 'upgrade'; roleId: string; roleName: string }
    | { action: 'create' }
  /**
   * True when the deck has no role for attendees to be seated in. Publishing a
   * demo deck with a presenter role and nothing else fails validation with
   * `presenter_role_requires_attendee_role`.
   */
  needsAttendee: boolean
}

/**
 * Decides which roles the "All Demo Rubrics" bulk-add has to create or fix up.
 *
 * Presenter identity is `isPresenter`, not the name — that flag is what
 * `seedDemoPresenterRole` looks for and what publish validation counts, and an
 * author is free to rename the role. Falling back to the name covers decks built
 * before the demo evaluation toggle existed, whose Presenter role carries no
 * flag; those are upgraded in place rather than duplicated.
 *
 * Pass every role on the deck, read once before any writes — the roles stream
 * won't reflect a write that hasn't round-tripped yet.
 */
export function planDemoRoles(roles: PlannableRole[]): DemoRolePlan {
  const flagged = roles.find((role) => role.isPresenter)

  const legacy = flagged
    ? undefined
    : roles.find(
        (role) =>
          role.roleName.trim().toLowerCase() ===
          PRESENTER_ROLE_NAME.toLowerCase()
      )

  const presenter: DemoRolePlan['presenter'] = flagged
    ? { action: 'reuse', roleId: flagged.id }
    : legacy
      ? { action: 'upgrade', roleId: legacy.id, roleName: legacy.roleName }
      : { action: 'create' }

  // A role being upgraded still reads isPresenter === false here, so exclude it
  // by id — otherwise it counts as the seating role and the deck ends up with a
  // presenter and nothing else, which is exactly what fails validation.
  const presenterRoleId = 'roleId' in presenter ? presenter.roleId : undefined
  const hasSeatingRole = roles.some(
    (role) => role.id !== presenterRoleId && !role.isPresenter
  )

  return { presenter, needsAttendee: !hasSeatingRole }
}
